import { useMemo, useSyncExternalStore } from 'react';
import { moduleNavigation } from '@/config/navigation.custom';
import type { ModuleNavigationConfig } from '@/config/navigation.generated';
import { authService } from '@/services/auth/authService';
import { isFeatureEnabled, moduleEnableFeature, useFeatureValues } from '@/lib/moduleFeatures';

/**
 * The navigation a user may see: modules whose backend feature is on for the tenant, the items and
 * quick actions the user holds the permission for, and no category or module left empty. The menu
 * and the landing-page choices both read this one filter, so a page is never offered as a landing
 * page to someone the menu hides it from.
 */
export function visibleNavigation(
  modules: ModuleNavigationConfig[],
  featureValues: Record<string, string>,
  grantedPermissions: string[],
): ModuleNavigationConfig[] {
  const hasPermission = (permission?: string) =>
    !permission || grantedPermissions.includes(permission);

  return modules
    .filter(m => isFeatureEnabled(featureValues, moduleEnableFeature(m.module)))
    .map(m => ({
      ...m,
      quickActions: m.quickActions.filter(qa => hasPermission(qa.permission)),
      categories: m.categories
        .map(cat => ({ ...cat, items: cat.items.filter(item => hasPermission(item.permission)) }))
        .filter(cat => cat.items.length > 0),
    }))
    .filter(m => m.categories.length > 0);
}

/**
 * The sidebar's module list in alphabetical order of the label the user reads — translated, and
 * compared by the UI language's rules (Swedish Å, Ä, Ö after Z). Only the module list is sorted:
 * categories and items inside a module keep their declared order.
 */
export function sortModulesByLabel(
  modules: ModuleNavigationConfig[],
  labelOf: (module: ModuleNavigationConfig) => string,
  locale: string,
): ModuleNavigationConfig[] {
  return [...modules].sort((a, b) => labelOf(a).localeCompare(labelOf(b), locale));
}

const permissionsSubscribe = (cb: () => void) => authService.subscribePermissions(cb);
const permissionsSnapshot = () => authService.getPermissions();

/** {@link visibleNavigation} for the signed-in user, following permission and feature refreshes. */
export function useVisibleNavigation(): ModuleNavigationConfig[] {
  const featureValues = useFeatureValues();
  const grantedPermissions = useSyncExternalStore(permissionsSubscribe, permissionsSnapshot);
  return useMemo(
    () => visibleNavigation(moduleNavigation, featureValues, grantedPermissions),
    [featureValues, grantedPermissions],
  );
}
